import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost, getErrorMessage, bulkDeleteChats, bulkAssignChats } from '../../utils/api'
import { mapRawChat, QueueChat } from '../../hooks/useQueue'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import { QueueItem } from './QueueItem'
import { useBulkSelect } from '../../hooks/useBulkSelect'
import { BulkActions, BulkIcons, SelectCheckbox } from '../shared/BulkActionBar'
import { AssignAgentModal } from '../shared/AssignAgentModal'

/**
 * Missed Chats (nav page). Uses the SAME data feed (/queue?status=missed) and
 * the SAME card component (QueueItem) as the Chats > Missed tab, so the two are
 * visually consistent. "Missed" = a chat that was ever missed (missed_at set) —
 * it stays here even after it's closed.
 */
export const MissedChatsView: React.FC<{ mineOnly?: boolean }> = ({ mineOnly = false }) => {
  const navigate = useNavigate()
  const { currentAgent } = useAuth()
  const { addNotification } = useNotifications()
  const [allChats, setAllChats] = useState<QueueChat[]>([])
  const [loading, setLoading] = useState(true)
  const [pickingUp, setPickingUp] = useState<string | null>(null)

  const sel = useBulkSelect()
  const [bulkBusy, setBulkBusy] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  // "My chats only" → missed chats assigned to the current agent.
  const chats = mineOnly && currentAgent
    ? allChats.filter((c) => String(c.assignedAgentId ?? '') === String(currentAgent.id))
    : allChats
  const visibleIds = chats.map((c) => c.id)

  const handleBulkDelete = async () => {
    if (sel.count === 0) return
    if (!window.confirm(`Permanently delete ${sel.count} chat${sel.count === 1 ? '' : 's'} and all their messages? This cannot be undone.`)) return
    setBulkBusy(true)
    try {
      const res = await bulkDeleteChats(sel.selectedIds)
      addNotification(`Deleted ${res.deleted ?? sel.count} chat(s)`, 'success')
      sel.clear()
      await load()
    } catch (err) {
      addNotification(getErrorMessage(err, 'Could not delete the selected chats'), 'error')
    } finally {
      setBulkBusy(false)
    }
  }

  const handleBulkAssign = async (agentId: string | null) => {
    if (!agentId || sel.count === 0) return
    setBulkBusy(true)
    try {
      const res = await bulkAssignChats(sel.selectedIds, agentId)
      addNotification(`Assigned ${res.assigned ?? sel.count} chat(s)`, 'success')
      setAssignOpen(false)
      sel.clear()
      await load()
    } catch (err) {
      addNotification(getErrorMessage(err, 'Could not assign the selected chats'), 'error')
    } finally {
      setBulkBusy(false)
    }
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await apiGet<{ chats: any[]; total: number } | any[]>('/queue', { status: 'missed' })
      const raw = Array.isArray(resp) ? resp : ((resp as any).queue || (resp as any).chats || [])
      setAllChats(raw.map(mapRawChat))
    } catch (err) {
      console.error('Failed to load missed chats:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Carry the Missed context through, so the list beside the opened chat stays on
  // the Missed tab instead of snapping back to Queued.
  const handleSelectChat = (chatId: string) => navigate(`/queue/${chatId}?status=missed`)

  const handlePickup = async (chatId: string) => {
    if (!currentAgent) return
    setPickingUp(chatId)
    try {
      await apiPost(`/chats/${chatId}/assign`, { agentId: currentAgent.id })
      addNotification('Chat assigned successfully', 'success')
      navigate(`/my-chats/${chatId}`)
    } catch (err) {
      addNotification(getErrorMessage(err, 'Failed to pick up chat'), 'error')
    } finally {
      setPickingUp(null)
    }
  }

  return (
    <div className="h-full flex flex-col relative">
      {/* Title + tabs live in the Chats shell above; keep a slim refresh row. */}
      <div className="px-6 py-2.5 bg-surface border-b border-border flex items-center justify-between">
        <p className="text-xs text-txt-tertiary">Conversations no operator picked up in time</p>
        <button
          onClick={() => load()}
          className="p-2 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover border border-border transition-colors"
          title="Refresh"
        >
          <svg className={`w-4 h-4 ${loading ? 'animate-spin-slow' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
        </button>
      </div>

      {!loading && chats.length > 0 && (
        <div className="flex items-center gap-3 px-6 py-2 bg-surface-secondary/50 border-b border-border">
          <SelectCheckbox
            checked={sel.allSelected(visibleIds)}
            indeterminate={sel.count > 0}
            onChange={() => sel.toggleAll(visibleIds)}
            title="Select all"
          />
          <span className="text-xs text-txt-tertiary">
            {sel.count > 0 ? `${sel.count} selected` : `Select all ${chats.length}`}
          </span>
          {sel.count > 0 && (
            <div className="ml-auto">
              <BulkActions
                busy={bulkBusy}
                actions={[
                  { label: 'Assign', icon: BulkIcons.assign, onClick: () => setAssignOpen(true) },
                  { label: 'Delete', icon: BulkIcons.delete, onClick: handleBulkDelete, variant: 'danger' },
                ]}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {loading && chats.length === 0 ? (
          <div className="flex items-center justify-center h-48">
            <span className="text-sm text-txt-secondary">Loading missed chats...</span>
          </div>
        ) : chats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-6 py-16">
            <div className="w-16 h-16 rounded-2xl bg-surface-tertiary flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-txt-primary mb-1">No missed chats</h3>
            <p className="text-xs text-txt-tertiary max-w-[240px]">Every conversation has been picked up in time. Nice work.</p>
          </div>
        ) : (
          chats.map((chat) => (
            <div
              key={chat.id}
              className={`flex items-stretch ${sel.isSelected(chat.id) ? 'bg-brand-50/50 dark:bg-brand-900/15' : ''}`}
            >
              <div className="flex items-center pl-4 pr-1">
                <SelectCheckbox checked={sel.isSelected(chat.id)} onChange={() => sel.toggle(chat.id)} />
              </div>
              <div className="flex-1 min-w-0">
                <QueueItem
                  chat={chat}
                  isSelected={false}
                  onClick={() => handleSelectChat(chat.id)}
                  onPickup={!chat.assignedAgentId ? () => handlePickup(chat.id) : undefined}
                  isPickingUp={pickingUp === chat.id}
                />
              </div>
            </div>
          ))
        )}
      </div>

      <AssignAgentModal
        open={assignOpen}
        count={sel.count}
        noun="chat"
        busy={bulkBusy}
        onCancel={() => setAssignOpen(false)}
        onConfirm={handleBulkAssign}
      />
    </div>
  )
}
